import { BaseClient } from '../base-client';
import type { DecideBulkResult, PendingCount, PendingSkillApproval } from './types';

type PendingResponse = { data: PendingSkillApproval[] };
type CountResponse = { data: PendingCount };
type BulkDecideResponse = { data: DecideBulkResult };

/**
 * Client for the "Team Approvals" (skill ratings) page — mirrors
 * `TimesheetApprovalsClient`'s own shape. The "pending for me" query +
 * sidebar count live here; bulk decide is on the generic approvals client
 * (`ApprovalInstancesClient`) because the endpoint itself is entity-
 * agnostic — re-exported here as a thin wrapper for convenience, same
 * reasoning as the timesheet client's own `approveBulk`/`rejectBulk`.
 */
export class SkillRatingApprovalsClient extends BaseClient {
  /** Pending skill-rating approvals awaiting the caller's decision. No
   *  filter dimension needed — this queue isn't date/customer scoped. */
  pending(): Promise<PendingResponse> {
    return this.request('/api/user-skill-approvals/pending');
  }

  /** Pending count for the sidebar badge. */
  count(): Promise<CountResponse> {
    return this.request('/api/user-skill-approvals/count');
  }

  /** Approve every pending rating in the caller's queue. */
  approveAll(): Promise<BulkDecideResponse> {
    return this.request('/api/user-skill-approvals/approve-all', { method: 'POST', body: {} });
  }

  /** Approve many instances in one request. Per-instance failures are
   *  surfaced in `results` rather than aborting the whole batch. */
  approveBulk(instanceIds: string[]): Promise<BulkDecideResponse> {
    return this.request('/api/approval-instances/approve-bulk', {
      method: 'POST',
      body: { instanceIds },
    });
  }

  /** Reject many instances with ONE shared reason. */
  rejectBulk(instanceIds: string[], reason: string): Promise<BulkDecideResponse> {
    return this.request('/api/approval-instances/reject-bulk', {
      method: 'POST',
      body: { instanceIds, reason },
    });
  }
}
